import {
  Checkbox,
  Input,
  InputMasked,
  Phone,
  Select,
} from '@chiroup/components';
import SectionContainer from '../../../../layout/SectionContainer';
import SectionHeader from '../../../../layout/SectionHeader';

import {
  COUNTRY_CODES,
  Discipline,
  DisciplineInsuranceBenefit,
  Insurance,
  UserRoles,
  ValueOf,
} from '@chiroup/core';
import { useContext, useMemo, useState } from 'react';
import Accordion from '../../../../../components/common/Accordion';
import { MeContext } from '../../../../../contexts/me.context';
import DisciplineBenefitForm from './DiscipleBenefitForm';
import PatientDisciplineSelectButton from './PatientDisciplineSelectButton';
import dayjs from 'dayjs';

type Props = {
  value: Partial<Insurance>;
  patchValue: (values: Partial<Insurance>) => void;
  onChange: (
    key: keyof Partial<Insurance>,
  ) => (val: ValueOf<Insurance>) => void;
  disciplines?: Discipline[];
  setEmailError: React.Dispatch<React.SetStateAction<boolean>>;
};
const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

const setNestedValue = (
  originalValue: Partial<Insurance>,
  keyPath: string,
  newValue: any,
): Partial<Insurance> => {
  const keys = keyPath.split('.');
  const obj: Partial<Insurance> = JSON.parse(JSON.stringify(originalValue));
  let current: Partial<Insurance> | any = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (i === keys.length - 1) {
      current[key] = newValue;
    } else {
      current[key] = current[key] || {};
    }
    current = current[key];
  }

  return obj;
};

const PatientInsuranceBenefitForm: React.FC<Props> = ({
  value,
  patchValue,
  onChange,
  disciplines,
  setEmailError,
}) => {
  const { hasRole } = useContext(MeContext);
  const { me } = useContext(MeContext);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [showClaimInfo, setShowClaimInfo] = useState(false);
  const [showAdjustorInfo, setShowAdjustorInfo] = useState(false);
  const handleNestedChange = (parentKey: string, key: string, e: string) => {
    const keyPath = `${parentKey}.${key}`;
    if (key === 'email') {
      if (regex.test(e as string)) {
        setEmailError(false);
      } else {
        setEmailError(true);
      }
    }
    const updatedValue = setNestedValue(value, keyPath, e);
    patchValue(updatedValue);
  };

  const formPolicyEndDate = useMemo(() => {
    if (
      (typeof value.policyEndDate === 'string' &&
        value.policyEndDate.length === 13) ||
      typeof value.policyEndDate === 'number'
    ) {
      return dayjs(value.policyEndDate).format('MM/DD/YYYY');
    }
    return value?.policyEndDate;
  }, [value?.policyEndDate]);

  const toggleAccordion = (section: number) => {
    setOpenAccordions((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section],
    );
  };

  return (
    <div className="relative col-span-4 mt-4 border rounded-md ">
      <SectionContainer parentClassName="p-0 ">
        <SectionHeader
          title={
            <p className="text-base font-light text-gray-600">
              Insurance Benefits
            </p>
          }
          rightSide={
            hasRole([UserRoles.Admin, UserRoles.Biller, UserRoles.Staff]) && (
              <PatientDisciplineSelectButton
                value={value}
                patchValue={patchValue}
                disciplines={disciplines}
              />
            )
          }
        />
        <div className="p-4 space-y-1 z-100 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
          <InputMasked
            className="col-span-2"
            value={formPolicyEndDate}
            onChange={onChange('policyEndDate')}
            name="policyEndDate"
            label="Policy End Date"
            patternFormat="##/##/####"
            placeholder="MM/DD/YYYY"
          />
          <Input
            name="policyMaximum"
            className="col-span-2"
            inputMode={'decimal'}
            label="Policy Maximum"
            decimalPlaces={2}
            type="number"
            value={value?.policyMaximum}
            onChange={onChange('policyMaximum')}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 468 468"
                version="1.0"
                height={14}
                width={14}
                className="text-gray-500"
              >
                <g transform="translate(0 468) scale(.078 -.078)">
                  <path
                    fill="currentColor"
                    d="m2600 5595c0-237-3-305-12-305-7 0-63-5-125-10-140-12-299-49-398-91-11-4-49-20-85-35-304-124-635-421-789-706-33-63-80-169-88-202-35-143-41-258-23-421 37-323 180-591 420-785 77-62 83-66 116-87 28-18 244-126 279-140 27-10 103-36 115-38 19-5 100-27 120-35 59-20 254-55 418-75l52-7v-1031l-82 6c-76 5-198 30-223 45-5 4-12 7-15 8-71 14-269 163-285 215-4 11-10 19-14 19-12 0-75 133-95 200l-17 55-410-3-410-2 6-63c17-165 65-343 132-487 26-54 99-185 115-205 4-5 27-35 51-65 72-93 202-215 327-306 60-44 244-154 257-154 5 0 25-9 44-19 34-18 167-63 264-90 62-17 188-31 276-31h79v-650h750l2 332 3 333 85 6c121 9 341 58 430 96 8 3 18 7 22 7 3 1 10 4 14 8 4 5 13 8 20 8 16 0 162 70 249 119 39 22 77 44 85 48s20 12 27 17c7 6 36 27 64 46 70 47 221 181 267 237 20 24 47 57 60 71 36 42 132 200 162 267 104 232 133 496 79 720-11 44-21 89-24 100-7 28-110 235-132 264-193 254-402 410-708 524-138 51-348 95-495 102-58 3-129 8-158 11l-52 5 2 502 3 502 58-2c154-5 367-94 455-188 66-71 103-148 110-230l5-65h863l-4 38c-4 47-29 151-52 222-141 430-441 791-800 962-133 63-169 78-235 94-5 2-44 12-86 23s-123 25-180 31-112 13-121 16c-17 5-18 28-18 290v284h-750v-305zm0-1585v-450h-57c-52 1-84 6-159 23-30 7-158 59-164 66-3 3-18 13-35 21-54 28-151 128-186 192-32 59-34 69-34 158 0 69 6 111 20 154 56 167 269 281 528 285l87 1v-450zm975-1485c219-17 399-118 472-266 46-93 48-281 3-367-80-153-329-259-612-261l-88-1v448c0 247 3 452 7 456s37 4 73 2c36-3 101-8 145-11z"
                  />
                </g>
              </svg>
            }
          />
          <div className="col-span-2">
            <Input
              name="annualDeductible"
              inputMode={'decimal'}
              type="number"
              decimalPlaces={2}
              label="Annual deductible"
              value={value?.annualDeductible}
              onChange={onChange('annualDeductible')}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 468 468"
                  version="1.0"
                  height={14}
                  width={14}
                  className="text-gray-500"
                >
                  <g transform="translate(0 468) scale(.078 -.078)">
                    <path
                      fill="currentColor"
                      d="m2600 5595c0-237-3-305-12-305-7 0-63-5-125-10-140-12-299-49-398-91-11-4-49-20-85-35-304-124-635-421-789-706-33-63-80-169-88-202-35-143-41-258-23-421 37-323 180-591 420-785 77-62 83-66 116-87 28-18 244-126 279-140 27-10 103-36 115-38 19-5 100-27 120-35 59-20 254-55 418-75l52-7v-1031l-82 6c-76 5-198 30-223 45-5 4-12 7-15 8-71 14-269 163-285 215-4 11-10 19-14 19-12 0-75 133-95 200l-17 55-410-3-410-2 6-63c17-165 65-343 132-487 26-54 99-185 115-205 4-5 27-35 51-65 72-93 202-215 327-306 60-44 244-154 257-154 5 0 25-9 44-19 34-18 167-63 264-90 62-17 188-31 276-31h79v-650h750l2 332 3 333 85 6c121 9 341 58 430 96 8 3 18 7 22 7 3 1 10 4 14 8 4 5 13 8 20 8 16 0 162 70 249 119 39 22 77 44 85 48s20 12 27 17c7 6 36 27 64 46 70 47 221 181 267 237 20 24 47 57 60 71 36 42 132 200 162 267 104 232 133 496 79 720-11 44-21 89-24 100-7 28-110 235-132 264-193 254-402 410-708 524-138 51-348 95-495 102-58 3-129 8-158 11l-52 5 2 502 3 502 58-2c154-5 367-94 455-188 66-71 103-148 110-230l5-65h863l-4 38c-4 47-29 151-52 222-141 430-441 791-800 962-133 63-169 78-235 94-5 2-44 12-86 23s-123 25-180 31-112 13-121 16c-17 5-18 28-18 290v284h-750v-305zm0-1585v-450h-57c-52 1-84 6-159 23-30 7-158 59-164 66-3 3-18 13-35 21-54 28-151 128-186 192-32 59-34 69-34 158 0 69 6 111 20 154 56 167 269 281 528 285l87 1v-450zm975-1485c219-17 399-118 472-266 46-93 48-281 3-367-80-153-329-259-612-261l-88-1v448c0 247 3 452 7 456s37 4 73 2c36-3 101-8 145-11z"
                    />
                  </g>
                </svg>
              }
            />
          </div>
          <div className="col-span-2">
            <Input
              name="deductible"
              inputMode={'decimal'}
              type="number"
              decimalPlaces={2}
              label="Remaining deductible"
              value={value?.deductible}
              onChange={onChange('deductible')}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 468 468"
                  version="1.0"
                  height={14}
                  width={14}
                  className="text-gray-500"
                >
                  <g transform="translate(0 468) scale(.078 -.078)">
                    <path
                      fill="currentColor"
                      d="m2600 5595c0-237-3-305-12-305-7 0-63-5-125-10-140-12-299-49-398-91-11-4-49-20-85-35-304-124-635-421-789-706-33-63-80-169-88-202-35-143-41-258-23-421 37-323 180-591 420-785 77-62 83-66 116-87 28-18 244-126 279-140 27-10 103-36 115-38 19-5 100-27 120-35 59-20 254-55 418-75l52-7v-1031l-82 6c-76 5-198 30-223 45-5 4-12 7-15 8-71 14-269 163-285 215-4 11-10 19-14 19-12 0-75 133-95 200l-17 55-410-3-410-2 6-63c17-165 65-343 132-487 26-54 99-185 115-205 4-5 27-35 51-65 72-93 202-215 327-306 60-44 244-154 257-154 5 0 25-9 44-19 34-18 167-63 264-90 62-17 188-31 276-31h79v-650h750l2 332 3 333 85 6c121 9 341 58 430 96 8 3 18 7 22 7 3 1 10 4 14 8 4 5 13 8 20 8 16 0 162 70 249 119 39 22 77 44 85 48s20 12 27 17c7 6 36 27 64 46 70 47 221 181 267 237 20 24 47 57 60 71 36 42 132 200 162 267 104 232 133 496 79 720-11 44-21 89-24 100-7 28-110 235-132 264-193 254-402 410-708 524-138 51-348 95-495 102-58 3-129 8-158 11l-52 5 2 502 3 502 58-2c154-5 367-94 455-188 66-71 103-148 110-230l5-65h863l-4 38c-4 47-29 151-52 222-141 430-441 791-800 962-133 63-169 78-235 94-5 2-44 12-86 23s-123 25-180 31-112 13-121 16c-17 5-18 28-18 290v284h-750v-305zm0-1585v-450h-57c-52 1-84 6-159 23-30 7-158 59-164 66-3 3-18 13-35 21-54 28-151 128-186 192-32 59-34 69-34 158 0 69 6 111 20 154 56 167 269 281 528 285l87 1v-450zm975-1485c219-17 399-118 472-266 46-93 48-281 3-367-80-153-329-259-612-261l-88-1v448c0 247 3 452 7 456s37 4 73 2c36-3 101-8 145-11z"
                    />
                  </g>
                </svg>
              }
            />
          </div>
          <Checkbox
            label="Accept assignment?"
            value={value?.acceptAssignment}
            onChange={onChange('acceptAssignment')}
            className="col-span-1 sm:col-span-3"
          />
          <Checkbox
            label="Signature on file"
            value={value?.signatureOnFile}
            onChange={onChange('signatureOnFile')}
            className="col-span-1 sm:col-span-3"
          />
          <Checkbox
            label="Release of patient information"
            value={value?.releaseOfPatientInformation}
            onChange={onChange('releaseOfPatientInformation')}
            className="col-span-1 sm:col-span-3"
          />
          <div className="col-span-4 w-full">
            <Accordion
              title="Property & Casualty Insurance"
              open={showClaimInfo}
              onToggle={() => setShowClaimInfo((prev) => !prev)}
              hugSides
              border
            >
              <div className="grid grid-cols-4 w-full gap-2">
                <Select
                  label="Patient ID Type"
                  name="patientIdType"
                  value={value?.propertyAndCasualtyInsurance?.patientIdType}
                  onChange={(e) =>
                    handleNestedChange(
                      'propertyAndCasualtyInsurance',
                      'patientIdType',
                      e,
                    )
                  }
                  options={[
                    { text: 'None', value: 'none' },
                    { text: 'SSN', value: 'ssn' },
                    { text: 'Member ID', value: 'memberId' },
                  ]}
                  className="col-span-2"
                  limit={1}
                />
                <Input
                  label="Claim number"
                  name="claimNumber"
                  value={value?.propertyAndCasualtyInsurance?.claimNumber}
                  onChange={(e) =>
                    handleNestedChange(
                      'propertyAndCasualtyInsurance',
                      'claimNumber',
                      e,
                    )
                  }
                  className="col-span-2"
                />
                <Input
                  label="Contact name"
                  name="contactName"
                  value={value?.propertyAndCasualtyInsurance?.contactName}
                  onChange={(e) =>
                    handleNestedChange(
                      'propertyAndCasualtyInsurance',
                      'contactName',
                      e,
                    )
                  }
                  className="col-span-4"
                />
                <Phone
                  className="col-span-2"
                  name="claimContactPhone"
                  label="Phone number"
                  value={value?.propertyAndCasualtyInsurance?.claimContactPhone}
                  onChange={(e) =>
                    handleNestedChange(
                      'propertyAndCasualtyInsurance',
                      'claimContactPhone',
                      e,
                    )
                  }
                  countryCode={
                    COUNTRY_CODES[me.selectedClinic?.country || 'USA']
                  }
                />
                <InputMasked
                  name="contactPhoneExtension"
                  label="Extension"
                  value={value?.propertyAndCasualtyInsurance?.extension}
                  onChange={(e) =>
                    handleNestedChange(
                      'propertyAndCasualtyInsurance',
                      'extension',
                      e,
                    )
                  }
                  className="col-span-2"
                  patternFormat="######"
                  numericOptions={{ numeral: true }}
                />
                {value?.propertyAndCasualtyInsurance?.patientIdType ===
                  'ssn' && (
                  <Input
                    label="SSN"
                    name="ssn"
                    type="number"
                    maxLength={9}
                    value={value?.propertyAndCasualtyInsurance?.ssn}
                    onChange={(e) =>
                      handleNestedChange(
                        'propertyAndCasualtyInsurance',
                        'ssn',
                        e,
                      )
                    }
                    className="col-span-2 px-2"
                  />
                )}
                {value?.propertyAndCasualtyInsurance?.patientIdType ===
                  'memberId' && (
                  <Input
                    label="Member Id"
                    name="memberId"
                    value={value?.propertyAndCasualtyInsurance?.memberId}
                    onChange={(e) =>
                      handleNestedChange(
                        'propertyAndCasualtyInsurance',
                        'memberId',
                        e,
                      )
                    }
                    className="col-span-2 px-2"
                  />
                )}
              </div>
            </Accordion>
          </div>
          <div className="col-span-4 w-full">
            <Accordion
              title="Adjustor Information"
              open={showAdjustorInfo}
              onToggle={() => setShowAdjustorInfo((prev) => !prev)}
              hugSides
              border
              hugTop
            >
              <div className="grid grid-cols-4 w-full gap-2">
                <Input
                  label="Name"
                  name="adjustorName"
                  value={value?.adjustorInformation?.name}
                  onChange={(e) =>
                    handleNestedChange('adjustorInformation', 'name', e)
                  }
                  className="col-span-4 px-2"
                />
                <Phone
                  className="col-span-2 pl-2"
                  name="phone"
                  label="Phone number"
                  value={value.adjustorInformation?.phone}
                  onChange={(e) =>
                    handleNestedChange('adjustorInformation', 'phone', e)
                  }
                  countryCode={
                    COUNTRY_CODES[me.selectedClinic?.country || 'USA']
                  }
                />
                <InputMasked
                  className="col-span-2 pr-2"
                  label="Extension"
                  name="extension"
                  value={value.adjustorInformation?.extension}
                  onChange={(e) =>
                    handleNestedChange('adjustorInformation', 'extension', e)
                  }
                  patternFormat="######"
                  numericOptions={{ numeral: true }}
                />
                <div className="flex flex-col col-span-2 pl-2">
                  <Input
                    label="Email address"
                    name="email"
                    onChange={(e) =>
                      handleNestedChange('adjustorInformation', 'email', e)
                    }
                    value={value.adjustorInformation?.email}
                  />
                  {value?.adjustorInformation?.email &&
                    !regex.test(value?.adjustorInformation?.email || '') && (
                      <span className="mt-2 text-sm text-red-500">
                        Please enter valid email
                      </span>
                    )}
                </div>
              </div>
            </Accordion>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-4">
            {value.disciplineBenefits?.map(
              (discipline: DisciplineInsuranceBenefit, i: number) => (
                <Accordion
                  key={discipline.disciplineId}
                  title={
                    disciplines?.find((d) => d.ID === discipline.disciplineId)
                      ?.name || ''
                  }
                  onToggle={() => {
                    toggleAccordion(discipline.disciplineId);
                  }}
                  open={openAccordions.includes(discipline.disciplineId)}
                >
                  <DisciplineBenefitForm
                    value={value}
                    patchValue={patchValue}
                    index={i}
                  />
                </Accordion>
              ),
            )}
          </div>
        </div>
      </SectionContainer>
    </div>
  );
};

export default PatientInsuranceBenefitForm;
